import React, { useState } from 'react';
import { useForm } from "react-hook-form";
import axios from 'axios';
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";

import { env } from './const';
import { setUserSession } from '../utils/common';
import { applyServerLanguage } from '../../i18n';
import AuthLayout from '../auth/AuthLayout';
import AuthField from '../auth/AuthField';
import { useAuthMotion } from '../auth/authMotion';
import styles from '../auth/AuthLayout.module.css';

// The app-store block, passed to AuthLayout as `brandFooter` so it stays on
// Login and renders on the dark brand panel rather than under the form.
// only — the other public pages share the shell but not this.
const MobileAppLinks = () => (
    <div className={styles.footer}>
        <div className={styles.stores}>
            <div className={styles.store}>
                {/* alt="" because the QR duplicates the link beside it. These
                    PNGs are 3150x3150 / ~382KB each for a 104px slot, so they
                    are deferred here and hidden below 768px — a QR is useless
                    on the phone you would scan it with. */}
                <img
                    className={styles.qr}
                    src='/dist/img/iPoutryAppStore.png'
                    alt=''
                    width={104}
                    height={104}
                    loading='lazy'
                    decoding='async'
                />
                <a
                    href='https://apps.apple.com/in/app/ipoultry/id6463606473'
                    target='_blank'
                    rel='noreferrer'
                >
                    <img className={styles.badge} src='/dist/img/appStore.png' alt='App Store' />
                </a>
            </div>
            <div className={styles.store}>
                <img
                    className={styles.qr}
                    src='/dist/img/iPoultry_playstore.png'
                    alt=''
                    width={104}
                    height={104}
                    loading='lazy'
                    decoding='async'
                />
                <a
                    href='https://play.google.com/store/apps/details?id=com.ipoultry&hl=en&gl=US'
                    target='_blank'
                    rel='noreferrer'
                >
                    <img className={styles.badge} src='/dist/img/playStore.png' alt='Google Play' />
                </a>
            </div>
        </div>
    </div>
);

const Login = (props) => {
    const { t } = useTranslation(['auth', 'common']);
    const v = useAuthMotion();
    const shake = useAnimationControls();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({ mode: 'onChange' });

    // One piece of state, not two: the old code kept a separate `show` boolean
    // and set it to true on the SUCCESS path as well, so an empty error banner
    // flashed for a beat before the redirect.
    const [errorMessage, setErrorMessage] = useState('');

    // async so react-hook-form's isSubmitting tracks the request, which is what
    // disables the button and guards against a double submit.
    const onSubmit = async (data, e) => {
        setErrorMessage('');
        try {
            const res = await axios.post(env.produrl + '/login', data);
            setUserSession(
                res.data.token,
                res.data.userDetails[0],
                res.data.browserDetails,
                res.data.refreshToken
            );
            // Server-stored preference wins over this browser's localStorage,
            // so the user's language follows them across devices. A null here
            // (no migration / no preference) leaves the local choice alone.
            applyServerLanguage(res.data.userDetails[0] && res.data.userDetails[0].language);
            if (e && e.target && e.target.reset) e.target.reset();
            if (res.status === 200) {
                props.history.push('/dashboard');
            }
        } catch (err) {
            // On a network failure there is no `err.response` at all. The old
            // code read err.response.data.message unguarded, so it threw a
            // second time inside its own catch and the user saw nothing.
            setErrorMessage(
                (err && err.response && err.response.data && err.response.data.message) ||
                t('auth:login.genericError')
            );
            if (v.shake) shake.start(v.shake);
        }
    };

    return (
        <AuthLayout
            title={t('auth:login.title')}
            subtitle={t('auth:login.subtitle')}
            brandFooter={<MobileAppLinks />}
        >
            <motion.form onSubmit={handleSubmit(onSubmit)} animate={shake} noValidate>
                <AnimatePresence initial={false}>
                    {errorMessage && (
                        <motion.div
                            key='login-error'
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            style={{ overflow: 'hidden' }}
                        >
                            <div className={styles.banner} role='alert'>{errorMessage}</div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div variants={v.formContainer} initial='hidden' animate='visible'>
                    <motion.div variants={v.item}>
                        <AuthField
                            id='login-username'
                            label={t('auth:login.userLabel')}
                            icon='fa fa-user-circle'
                            placeholder={t('auth:login.userPlaceholder')}
                            autoComplete='username'
                            error={errors.username && t('auth:validation.enterUserName')}
                            {...register('username', { required: true })}
                        />
                    </motion.div>

                    <motion.div variants={v.item}>
                        <AuthField
                            id='login-password'
                            label={t('common:fields.password')}
                            icon='fas fa-lock'
                            type='password'
                            canReveal
                            revealLabel={t('auth:login.showPassword')}
                            hideRevealLabel={t('auth:login.hidePassword')}
                            placeholder={t('auth:login.passwordPlaceholder')}
                            autoComplete='current-password'
                            error={errors.password && t('auth:validation.enterPassword')}
                            {...register('password', { required: true })}
                        />
                    </motion.div>

                    <motion.div className={styles.actions} variants={v.item}>
                        <NavLink exact to='/forgotpassword' className={styles.link}>
                            {t('auth:login.forgotPassword')}
                        </NavLink>
                    </motion.div>

                    <motion.div variants={v.item}>
                        <motion.button
                            type='submit'
                            className={styles.submit}
                            disabled={isSubmitting}
                            aria-busy={isSubmitting}
                            whileHover={isSubmitting ? undefined : { scale: 1.02 }}
                            whileTap={isSubmitting ? undefined : { scale: 0.98 }}
                        >
                            {/* No spinner under reduced motion: the scoped
                                prefers-reduced-motion rule would freeze it
                                mid-rotation, which reads as a hung page. The
                                label change carries the state instead. */}
                            {isSubmitting && !v.reduced && (
                                <span className={styles.spinner} aria-hidden='true' />
                            )}
                            {isSubmitting ? t('auth:login.signingIn') : t('auth:login.signIn')}
                        </motion.button>
                    </motion.div>
                </motion.div>
            </motion.form>
        </AuthLayout>
    );
};

export default Login;
